import React, { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { getHeadshot } from './../../../../shared/utilities/getHeadshot.function'
import { getRaceThresholdForMilestone } from './../../../../constants/race-to-the-ring.constants'
import Store from './../../store'

import './RaceColumn.component.scss'

const getNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getMilestoneValue = (milestone, key) => {
  if (!milestone) return undefined
  if (typeof milestone.get === 'function') {
    const value = milestone.get(key)
    if (typeof value !== 'undefined') return value
  }
  if (typeof milestone.raw === 'function') {
    const raw = milestone.raw()
    if (raw && Object.prototype.hasOwnProperty.call(raw, key)) return raw[key]
  }
  if (typeof milestone === 'object' && milestone !== null) {
    return milestone[key]
  }
  return undefined
}

const getWinnerName = milestone => {
  const first = getMilestoneValue(milestone, 'mi_fname') ?? ''
  const last = getMilestoneValue(milestone, 'mi_lname') ?? ''
  const name = `${first} ${last}`.trim()
  return name || 'Who will it be?'
}

const getWinnerHeadshot = milestone => {
  const userId = getMilestoneValue(milestone, 'user_id')
  const user = Store.getUserById(userId)
  const userPicture =
    (typeof user?.get === 'function' && user.get('u_picture')) ??
    (typeof user?.raw === 'function' ? user.raw()?.u_picture : undefined)
  const milestonePicture =
    getMilestoneValue(milestone, 'u_picture') ??
    getMilestoneValue(milestone, 'pic') ??
    getMilestoneValue(milestone, 'image')

  return getHeadshot({
    u_picture: userPicture ?? milestonePicture,
    id: userId,
  })
}

const formatPoints = value => {
  const num = getNumber(value)
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}MM`
  if (num >= 1000) return `${(num / 1000).toFixed(0)}K`
  return num.toLocaleString()
}

const RaceColumn = ({ raceKey, title, subtitle }) => {
  const columnRef = useRef(null)

  const racers = Array.isArray(Store?.Racers?.[raceKey]) ? Store.Racers[raceKey] : []

  // Sort racers by mi_value (highest first)
  const sortedRacers = racers.slice().sort((a, b) => {
    const aValue = getNumber(getMilestoneValue(a, 'mi_value'))
    const bValue = getNumber(getMilestoneValue(b, 'mi_value'))
    return bValue - aValue
  })

  // Height matching logic for desktop (when columns are side-by-side)
  useEffect(() => {
    if (Store.isLoading) return

    const matchColumnHeights = () => {
      // Only match heights when columns are not wrapped (desktop view)
      const columns = document.querySelectorAll('.race-column')
      if (columns.length < 3) return

      // Check if columns are wrapped (mobile view)
      const firstCol = columns[0]
      const secondCol = columns[1]
      if (!firstCol || !secondCol) return

      const firstRect = firstCol.getBoundingClientRect()
      const secondRect = secondCol.getBoundingClientRect()

      // If columns are stacked (wrapped), don't match heights
      // Allow small tolerance for rounding differences
      if (secondRect.top > firstRect.bottom + 10) {
        // Reset heights on mobile
        columns.forEach(col => {
          col.style.height = 'auto'
        })
        return
      }

      // Reset heights first
      columns.forEach(col => {
        col.style.height = 'auto'
      })

      // Find max height after reset
      let maxHeight = 0
      columns.forEach(col => {
        const height = col.offsetHeight
        if (height > maxHeight) maxHeight = height
      })

      // Apply max height to all columns
      if (maxHeight > 0) {
        columns.forEach(col => {
          col.style.height = `${maxHeight}px`
        })
      }
    }

    // Match heights after a short delay to ensure DOM is updated
    const timeoutId = setTimeout(matchColumnHeights, 150)

    // Also match on window resize with debounce
    let resizeTimeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(matchColumnHeights, 150)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [Store.isLoading, sortedRacers.length])

  // Hide column if no contestants (after all hooks)
  if (sortedRacers.length === 0) {
    return null
  }

  const renderRacer = (racer, index) => {
    const name = getWinnerName(racer)
    const headshot = getWinnerHeadshot(racer)
    const value = getNumber(getMilestoneValue(racer, 'mi_value'))
    const threshold = getRaceThresholdForMilestone(racer)
    const progress = threshold > 0 ? Math.min((value / threshold) * 100, 100) : 0

    return (
      <div key={`racer-${raceKey}-${index}`} className='race-column__card'>
        <div className='race-column__recipient'>
          <div className='race-column__img'>
            <div className='img-wrapper'>
              <img src={headshot} className='mx-auto d-block img-fluid rounded-circle' alt={name} />
            </div>
          </div>
          <div className='race-column__details'>
            <div>
              <h6>{name}</h6>
            </div>
            <div className='race-column__points'>
              {formatPoints(value)} / {formatPoints(threshold)} pts
            </div>
            <div className='race-column__progress'>
              <div className='race-column__progress-bar' style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={columnRef} className='race-column'>
      <div className={`race-column__wrapper race-column--${raceKey}`}>
        <div className='race-column__label'>
          <h5>{title || `${raceKey.charAt(0).toUpperCase() + raceKey.slice(1)} Race`}</h5>
          {subtitle && <p className='race-column__subtitle'>{subtitle}</p>}
        </div>
        <div className='race-column__section'>
          <div className='race-column__section-title'>Contestants</div>
          {sortedRacers.map((racer, index) => renderRacer(racer, index))}
        </div>
      </div>
    </div>
  )
}

export default observer(RaceColumn)
